import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { ConfirmationDialog } from '../ui/confirmation-dialog'
import {
  Building2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Plus,
  RefreshCw,
  MoreHorizontal,
  Pencil,
  Archive,
  ArchiveRestore,
  Loader2,
} from 'lucide-react'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { useSessionStore } from '../../stores/useSessionStore'
import { SimpleClient } from '../../model/Client'
import { DialogManager } from '../../context'
import ClientEndpoints from '../../endpoints/ClientEndpoints'
import { InterpretTimezoneCode } from '../../common/Timezones'

export const ClientList: React.FC = () => {
  const [includeArchived, setIncludeArchived] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [clientToArchive, setClientToArchive] = useState<SimpleClient | null>(null)
  const [isArchiving, setIsArchiving] = useState(false)
  const navigate = useNavigate()

  const { clients, refreshClients, isLoading } = useApplicationStore()
  const { hasAdmin, session, loggedIn, onLoad } = useSessionStore()

  useEffect(() => {
    onLoad()
  }, [onLoad])

  useEffect(() => {
    refreshClients()
  }, [refreshClients])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, includeArchived])

  const filteredAndSortedClients = clients
    .filter((client) => {
      const matchesSearch =
        searchTerm === '' ||
        client.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesArchived = includeArchived || !client.dateArchived
      return matchesSearch && matchesArchived
    })
    .sort((a, b) => {
      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()
      if (sortOrder === 'asc') {
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
      } else {
        return nameA > nameB ? -1 : nameA < nameB ? 1 : 0
      }
    })

  const totalItems = filteredAndSortedClients.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedClients = filteredAndSortedClients.slice(startIndex, endIndex)
  const activeCount = clients.filter((c) => !c.dateArchived).length

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(parseInt(newSize))
    setCurrentPage(1)
  }

  const handleArchive = (client: SimpleClient) => {
    setClientToArchive(client)
    setShowArchiveDialog(true)
  }

  const confirmArchive = async () => {
    if (!clientToArchive) return

    setIsArchiving(true)
    try {
      await ClientEndpoints.setClientArchivedStatus(clientToArchive.id, {
        field: 'archived',
        value: '' + !clientToArchive.dateArchived,
      })
      await refreshClients()
      setShowArchiveDialog(false)
      setClientToArchive(null)
    } catch (err) {
      DialogManager.showAlert('Error: ' + err)
    } finally {
      setIsArchiving(false)
    }
  }

  const cancelArchive = () => {
    setShowArchiveDialog(false)
    setClientToArchive(null)
    setIsArchiving(false)
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Clients</h1>
              <p className="text-sm text-muted-foreground">
                {activeCount} active client{activeCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => refreshClients()}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {hasAdmin() && (
            <Button onClick={() => navigate('/client/new')}>
              <Plus className="mr-2 h-4 w-4" />
              New Client
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeArchived"
                checked={includeArchived}
                onCheckedChange={(checked) => setIncludeArchived(checked === true)}
              />
              <label
                htmlFor="includeArchived"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 whitespace-nowrap"
              >
                Include Archived
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading() ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Loading clients...</p>
            </div>
          ) : paginatedClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Building2 className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-4 text-sm font-medium">No clients found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Get started by creating your first client'}
              </p>
              {!searchTerm && hasAdmin() && (
                <Button className="mt-4" onClick={() => navigate('/client/new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Client
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[30%]">
                      <Button
                        variant="ghost"
                        className="h-auto p-0 font-semibold hover:bg-transparent"
                        onClick={handleSortToggle}
                      >
                        Name
                        {sortOrder === 'asc' ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </Button>
                    </TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Timezone</TableHead>
                    <TableHead className="text-center">Badges</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className={`cursor-pointer transition-colors ${client.dateArchived ? 'bg-yellow-50/50 hover:bg-yellow-50' : ''}`}
                      onClick={() => navigate(`/client/${client.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                            {client.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-primary hover:underline">
                            {client.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.region?.name || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {InterpretTimezoneCode(client.timezone)}
                      </TableCell>
                      <TableCell className="text-center">
                        {client.clientBadgesEnabled ? (
                          <Badge variant="default" className="text-xs">Enabled</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {client.dateArchived ? (
                          <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                            Archived
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 hover:bg-green-50">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {hasAdmin() && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/client/${client.id}`)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleArchive(client)}
                                className={client.dateArchived ? '' : 'text-destructive focus:text-destructive'}
                              >
                                {client.dateArchived ? (
                                  <>
                                    <ArchiveRestore className="mr-2 h-4 w-4" />
                                    Unarchive
                                  </>
                                ) : (
                                  <>
                                    <Archive className="mr-2 h-4 w-4" />
                                    Archive
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        type={clientToArchive?.dateArchived ? 'unarchive' : 'archive'}
        title={clientToArchive?.dateArchived ? 'Unarchive Client' : 'Archive Client'}
        itemName={clientToArchive?.name}
        description={
          clientToArchive?.dateArchived
            ? `Are you sure you want to unarchive the client "${clientToArchive?.name}"? This will make the client active and available for new events.`
            : `Are you sure you want to archive the client "${clientToArchive?.name}"?`
        }
        warningContent={
          !clientToArchive?.dateArchived
            ? '<strong>Warning:</strong> Archived clients will no longer be available for new events, but existing events and data will remain intact.'
            : undefined
        }
        confirmText={clientToArchive?.dateArchived ? 'Unarchive Client' : 'Archive Client'}
        isLoading={isArchiving}
        onConfirm={confirmArchive}
        onCancel={cancelArchive}
      />
    </div>
  )
}
